/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { RouteResponseOfRoadFeatures } from '../models/RouteResponseOfRoadFeatures';
import type { RouteResponseOfRoadFeaturesWithBruksklasse } from '../models/RouteResponseOfRoadFeaturesWithBruksklasse';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class RouteService {

    /**
     * Calculate the optimal route for the given RouteRequest
     * @returns RouteResponseOfRoadFeatures Returns best route.
 * 
     * @throws ApiError
     */
    public static routeBest({
stops,
inputSrs,
outputSrs,
lang,
returnFields,
barriers,
streetNameHints,
encapsulateStreetNames,
allowTravelInZeroEmissionZone,
length,
weight,
height,
startTime,
avoidSituationIds,
avoidTrafficMessageTypes,
avoidRoadFeatureTypes,
avoidFeatureInstanceIds,
includeObstructedRoutes,
}: {
/**
 * A list of the included stops in the request. The stops need to be in the coordinate system corresponding to
 * InputSRS
 * ie: 236397.85827911127,6662445.118563663;237667.8608191164,6675145.143963715
 * 
 */
stops: string,
/**
 * Describes which coordinate system points in "stops" are using. Default is UTM 33N ( EPSG 25833 )
 * 
 */
inputSrs?: 'EPSG_25833' | 'EPSG_4326',
/**
 * Describes which coordinate system geometry in routedescription are using. Default is UTM 33N ( EPSG 25833 )
 * 
 */
outputSrs?: 'EPSG_25833' | 'EPSG_4326',
/**
 * Language for output directions-response.
 * 
 */
lang?: 'English' | 'Norwegian' | 'Swedish' | 'Deutch' | 'Finnish',
/**
 * List of additional Datafields to include in response
 * The textual directions for the route is always returned.
 * Note: SimpleGeometry is not implemented at the moment and will return the same result as Geometry.
 * 
 */
returnFields?: Array<any> | null,
/**
 * A list of the included barriers in the request. Barriers of type coordinate need to be in the UTM33N (EPSG:32633 or
 * EPSG:25833) coordinate system
 * 
 */
barriers?: string | null,
/**
 * streetnames for stops separated by ; (semicolon). By specifying a streetname for each stop in the stops-parameter
 * the router will try to snap the stops to streets with correct name
 * 
 */
streetNameHints?: string | null,
/**
 * If set to true, streetnames in returned directions are encapsulated in []. Ex: Go north on [skogsbilsveg] toward
 * [privat veg], Default=false
 * 
 */
encapsulateStreetNames?: boolean | null,
/**
 * Set this parameter to false to disallow travel in ZeroEmissionZones Default=true
 * 
 */
allowTravelInZeroEmissionZone?: boolean | null,
/**
 * If set, the route is calculated only using roads that allow a vehicle of the given length to pass. meters,
 * Default=0.0
 * 
 */
length?: number | null,
/**
 * If set, the route is calculated only using roads allowed to use with a vehicle of the specified weight in metric
 * tons. Should be given as number e.g. 15.6, Default=0.0
 * 
 */
weight?: number | null,
/**
 * If set, the route is calculated only using roads that allow a vehicle of the given height to pass. meters,
 * Default=0.0
 * 
 */
height?: number | null,
/**
 * format: yyyyMMddHHmmss Set the starttime of the route (this affects delays, dynamic roadinformation and
 * route-choices due to traffic)
 * 
 */
startTime?: string | null,
/**
 * Set this parameter to avoid specific traffic situations, with a comma separated list of Situation IDs
 * 
 */
avoidSituationIds?: string | null,
/**
 * Set this parameter to avoid traffic messages of certain types, ie 'roadclosed' to avoid temporarily closed roads.
 * NB. Not yet fully supported.
 * 
 */
avoidTrafficMessageTypes?: string | null,
/**
 * Set this parameter to avoid all road features of the specified road feature type, ie 'Bomstasjon' to avoid toll stations.
 */
avoidRoadFeatureTypes?: Array<any> | null,
/**
 * Set this parameter to avoid a road feature instance/object, ie 'TrafficInformationSimple-SituationId'. Using the Attribute AvoidFeatureInstanceId
 */
avoidFeatureInstanceIds?: string | null,
/**
 * Set this parameter to include obstructed routes. Obstructed routes are calculated without restrictions, but cannot be driven because they are obstructed.
 * Default = false
 */
includeObstructedRoutes?: boolean,
}): CancelablePromise<RouteResponseOfRoadFeatures> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/Route/best',
            query: {
                'Stops': stops,
                'InputSRS': inputSrs,
                'OutputSRS': outputSrs,
                'Lang': lang,
                'ReturnFields': returnFields,
                'Barriers': barriers,
                'StreetNameHints': streetNameHints,
                'EncapsulateStreetNames': encapsulateStreetNames,
                'AllowTravelInZeroEmissionZone': allowTravelInZeroEmissionZone,
                'Length': length,
                'Weight': weight,
                'Height': height,
                'StartTime': startTime,
                'AvoidSituationIds': avoidSituationIds,
                'AvoidTrafficMessageTypes': avoidTrafficMessageTypes,
                'AvoidRoadFeatureTypes': avoidRoadFeatureTypes,
                'AvoidFeatureInstanceIds': avoidFeatureInstanceIds,
                'IncludeObstructedRoutes': includeObstructedRoutes,
            },
        });
    }

    /**
     * Calculate a route prioritizing roads labeled as "tourist" roads.
     * @returns RouteResponseOfRoadFeatures A scenic route potentially longer than the "best" route.
     * @throws ApiError
     */
    public static routeTourist({
stops,
inputSrs,
outputSrs,
lang,
returnFields,
barriers,
streetNameHints,
encapsulateStreetNames,
allowTravelInZeroEmissionZone,
length,
weight,
height,
startTime,
avoidSituationIds,
avoidTrafficMessageTypes,
avoidRoadFeatureTypes,
avoidFeatureInstanceIds,
includeObstructedRoutes,
}: {
/**
 * A list of the included stops in the request. The stops need to be in the coordinate system corresponding to
 * InputSRS
 * ie: 236397.85827911127,6662445.118563663;237667.8608191164,6675145.143963715
 * 
 */
stops: string,
/**
 * Describes which coordinate system points in "stops" are using. Default is UTM 33N ( EPSG 25833 )
 * 
 */
inputSrs?: 'EPSG_25833' | 'EPSG_4326',
/**
 * Describes which coordinate system geometry in routedescription are using. Default is UTM 33N ( EPSG 25833 )
 * 
 */
outputSrs?: 'EPSG_25833' | 'EPSG_4326',
/**
 * Language for output directions-response.
 * 
 */
lang?: 'English' | 'Norwegian' | 'Swedish' | 'Deutch' | 'Finnish',
/**
 * List of additional Datafields to include in response
 * The textual directions for the route is always returned.
 * Note: SimpleGeometry is not implemented at the moment and will return the same result as Geometry.
 * 
 */
returnFields?: Array<any> | null,
/**
 * A list of the included barriers in the request. Barriers of type coordinate need to be in the UTM33N (EPSG:32633 or
 * EPSG:25833) coordinate system
 * 
 */
barriers?: string | null,
/**
 * streetnames for stops separated by ; (semicolon). By specifying a streetname for each stop in the stops-parameter
 * the router will try to snap the stops to streets with correct name
 * 
 */
streetNameHints?: string | null,
/**
 * If set to true, streetnames in returned directions are encapsulated in []. Ex: Go north on [skogsbilsveg] toward
 * [privat veg], Default=false
 * 
 */
encapsulateStreetNames?: boolean | null,
/**
 * Set this parameter to false to disallow travel in ZeroEmissionZones Default=true
 * 
 */
allowTravelInZeroEmissionZone?: boolean | null,
/**
 * If set, the route is calculated only using roads that allow a vehicle of the given length to pass. meters,
 * Default=0.0
 * 
 */
length?: number | null,
/**
 * If set, the route is calculated only using roads allowed to use with a vehicle of the specified weight in metric
 * tons. Should be given as number e.g. 15.6, Default=0.0
 * 
 */
weight?: number | null,
/**
 * If set, the route is calculated only using roads that allow a vehicle of the given height to pass. meters,
 * Default=0.0
 * 
 */
height?: number | null,
/**
 * format: yyyyMMddHHmmss Set the starttime of the route (this affects delays, dynamic roadinformation and
 * route-choices due to traffic)
 * 
 */
startTime?: string | null,
/**
 * Set this parameter to avoid specific traffic situations, with a comma separated list of Situation IDs
 * 
 */
avoidSituationIds?: string | null,
/**
 * Set this parameter to avoid traffic messages of certain types, ie 'roadclosed' to avoid temporarily closed roads.
 * NB. Not yet fully supported.
 * 
 */
avoidTrafficMessageTypes?: string | null,
/**
 * Set this parameter to avoid all road features of the specified road feature type, ie 'Bomstasjon' to avoid toll stations.
 */
avoidRoadFeatureTypes?: Array<any> | null,
/**
 * Set this parameter to avoid a road feature instance/object, ie 'TrafficInformationSimple-SituationId'. Using the Attribute AvoidFeatureInstanceId
 */
avoidFeatureInstanceIds?: string | null,
/**
 * Set this parameter to include obstructed routes. Obstructed routes are calculated without restrictions, but cannot be driven because they are obstructed.
 * Default = false
 */
includeObstructedRoutes?: boolean,
}): CancelablePromise<RouteResponseOfRoadFeatures> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/Route/tourist',
            query: {
                'Stops': stops,
                'InputSRS': inputSrs,
                'OutputSRS': outputSrs,
                'Lang': lang,
                'ReturnFields': returnFields,
                'Barriers': barriers,
                'StreetNameHints': streetNameHints,
                'EncapsulateStreetNames': encapsulateStreetNames,
                'AllowTravelInZeroEmissionZone': allowTravelInZeroEmissionZone,
                'Length': length,
                'Weight': weight,
                'Height': height,
                'StartTime': startTime,
                'AvoidSituationIds': avoidSituationIds,
                'AvoidTrafficMessageTypes': avoidTrafficMessageTypes,
                'AvoidRoadFeatureTypes': avoidRoadFeatureTypes,
                'AvoidFeatureInstanceIds': avoidFeatureInstanceIds,
                'IncludeObstructedRoutes': includeObstructedRoutes,
            },
        });
    }

    /**
     * Note: Not currently implemented.
     * @returns RouteResponseOfRoadFeatures 
     * @throws ApiError
     */
    public static routeBike({
stops,
powerEffort,
bikePathUsage,
inputSrs,
outputSrs,
lang,
returnFields,
barriers,
streetNameHints,
encapsulateStreetNames,
allowTravelInZeroEmissionZone,
length,
weight,
height,
startTime,
avoidSituationIds,
avoidTrafficMessageTypes,
avoidRoadFeatureTypes,
avoidFeatureInstanceIds,
includeObstructedRoutes,
}: {
/**
 * A list of the included stops in the request. The stops need to be in the coordinate system corresponding to
 * InputSRS
 * ie: 236397.85827911127,6662445.118563663;237667.8608191164,6675145.143963715
 * 
 */
stops: string,
powerEffort?: number,
bikePathUsage?: number,
/**
 * Describes which coordinate system points in "stops" are using. Default is UTM 33N ( EPSG 25833 )
 * 
 */
inputSrs?: 'EPSG_25833' | 'EPSG_4326',
/**
 * Describes which coordinate system geometry in routedescription are using. Default is UTM 33N ( EPSG 25833 )
 * 
 */
outputSrs?: 'EPSG_25833' | 'EPSG_4326',
/**
 * Language for output directions-response.
 * 
 */
lang?: 'English' | 'Norwegian' | 'Swedish' | 'Deutch' | 'Finnish',
/**
 * List of additional Datafields to include in response
 * The textual directions for the route is always returned.
 * Note: SimpleGeometry is not implemented at the moment and will return the same result as Geometry.
 * 
 */
returnFields?: Array<any> | null,
/**
 * A list of the included barriers in the request. Barriers of type coordinate need to be in the UTM33N (EPSG:32633 or
 * EPSG:25833) coordinate system
 * 
 */
barriers?: string | null,
/**
 * streetnames for stops separated by ; (semicolon). By specifying a streetname for each stop in the stops-parameter
 * the router will try to snap the stops to streets with correct name
 * 
 */
streetNameHints?: string | null,
/**
 * If set to true, streetnames in returned directions are encapsulated in []. Ex: Go north on [skogsbilsveg] toward
 * [privat veg], Default=false
 * 
 */
encapsulateStreetNames?: boolean | null,
/**
 * Set this parameter to false to disallow travel in ZeroEmissionZones Default=true
 * 
 */
allowTravelInZeroEmissionZone?: boolean | null,
/**
 * If set, the route is calculated only using roads that allow a vehicle of the given length to pass. meters,
 * Default=0.0
 * 
 */
length?: number | null,
/**
 * If set, the route is calculated only using roads allowed to use with a vehicle of the specified weight in metric
 * tons. Should be given as number e.g. 15.6, Default=0.0
 * 
 */
weight?: number | null,
/**
 * If set, the route is calculated only using roads that allow a vehicle of the given height to pass. meters,
 * Default=0.0
 * 
 */
height?: number | null,
/**
 * format: yyyyMMddHHmmss Set the starttime of the route (this affects delays, dynamic roadinformation and
 * route-choices due to traffic)
 * 
 */
startTime?: string | null,
/**
 * Set this parameter to avoid specific traffic situations, with a comma separated list of Situation IDs
 * 
 */
avoidSituationIds?: string | null,
/**
 * Set this parameter to avoid traffic messages of certain types, ie 'roadclosed' to avoid temporarily closed roads.
 * NB. Not yet fully supported.
 * 
 */
avoidTrafficMessageTypes?: string | null,
/**
 * Set this parameter to avoid all road features of the specified road feature type, ie 'Bomstasjon' to avoid toll stations.
 */
avoidRoadFeatureTypes?: Array<any> | null,
/**
 * Set this parameter to avoid a road feature instance/object, ie 'TrafficInformationSimple-SituationId'. Using the Attribute AvoidFeatureInstanceId
 */
avoidFeatureInstanceIds?: string | null,
/**
 * Set this parameter to include obstructed routes. Obstructed routes are calculated without restrictions, but cannot be driven because they are obstructed.
 * Default = false
 */
includeObstructedRoutes?: boolean,
}): CancelablePromise<RouteResponseOfRoadFeatures> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/Route/bike',
            query: {
                'PowerEffort': powerEffort,
                'BikePathUsage': bikePathUsage,
                'Stops': stops,
                'InputSRS': inputSrs,
                'OutputSRS': outputSrs,
                'Lang': lang,
                'ReturnFields': returnFields,
                'Barriers': barriers,
                'StreetNameHints': streetNameHints,
                'EncapsulateStreetNames': encapsulateStreetNames,
                'AllowTravelInZeroEmissionZone': allowTravelInZeroEmissionZone,
                'Length': length,
                'Weight': weight,
                'Height': height,
                'StartTime': startTime,
                'AvoidSituationIds': avoidSituationIds,
                'AvoidTrafficMessageTypes': avoidTrafficMessageTypes,
                'AvoidRoadFeatureTypes': avoidRoadFeatureTypes,
                'AvoidFeatureInstanceIds': avoidFeatureInstanceIds,
                'IncludeObstructedRoutes': includeObstructedRoutes,
            },
        });
    }

    /**
     * Calculate a optimal route given the BruksklasseRequest.
     * @returns RouteResponseOfRoadFeaturesWithBruksklasse A valid route according to https://datakatalogen.atlas.vegvesen.no/#/904/Bruksklasse,%20normaltransport
     * @throws ApiError
     */
    public static routeBruksklasseNormalTransport({
bruksklasse,
stops,
inputSrs,
outputSrs,
lang,
returnFields,
barriers,
streetNameHints,
encapsulateStreetNames,
allowTravelInZeroEmissionZone,
length,
weight,
height,
startTime,
avoidSituationIds,
avoidTrafficMessageTypes,
avoidRoadFeatureTypes,
avoidFeatureInstanceIds,
includeObstructedRoutes,
}: {
bruksklasse: 'Bk_6_28' | 'Bk_8_32' | 'Bk_T8_40' | 'Bk_T8_50' | 'Bk_10_42' | 'Bk_10_50',
/**
 * A list of the included stops in the request. The stops need to be in the coordinate system corresponding to
 * InputSRS
 * ie: 236397.85827911127,6662445.118563663;237667.8608191164,6675145.143963715
 * 
 */
stops: string,
/**
 * Describes which coordinate system points in "stops" are using. Default is UTM 33N ( EPSG 25833 )
 * 
 */
inputSrs?: 'EPSG_25833' | 'EPSG_4326',
/**
 * Describes which coordinate system geometry in routedescription are using. Default is UTM 33N ( EPSG 25833 )
 * 
 */
outputSrs?: 'EPSG_25833' | 'EPSG_4326',
/**
 * Language for output directions-response.
 * 
 */
lang?: 'English' | 'Norwegian' | 'Swedish' | 'Deutch' | 'Finnish',
/**
 * List of additional Datafields to include in response
 * The textual directions for the route is always returned.
 * Note: SimpleGeometry is not implemented at the moment and will return the same result as Geometry.
 * 
 */
returnFields?: Array<any> | null,
/**
 * A list of the included barriers in the request. Barriers of type coordinate need to be in the UTM33N (EPSG:32633 or
 * EPSG:25833) coordinate system
 * 
 */
barriers?: string | null,
/**
 * streetnames for stops separated by ; (semicolon). By specifying a streetname for each stop in the stops-parameter
 * the router will try to snap the stops to streets with correct name
 * 
 */
streetNameHints?: string | null,
/**
 * If set to true, streetnames in returned directions are encapsulated in []. Ex: Go north on [skogsbilsveg] toward
 * [privat veg], Default=false
 * 
 */
encapsulateStreetNames?: boolean | null,
/**
 * Set this parameter to false to disallow travel in ZeroEmissionZones Default=true
 * 
 */
allowTravelInZeroEmissionZone?: boolean | null,
/**
 * If set, the route is calculated only using roads that allow a vehicle of the given length to pass. meters,
 * Default=0.0
 * 
 */
length?: number | null,
/**
 * If set, the route is calculated only using roads allowed to use with a vehicle of the specified weight in metric
 * tons. Should be given as number e.g. 15.6, Default=0.0
 * 
 */
weight?: number | null,
/**
 * If set, the route is calculated only using roads that allow a vehicle of the given height to pass. meters,
 * Default=0.0
 * 
 */
height?: number | null,
/**
 * format: yyyyMMddHHmmss Set the starttime of the route (this affects delays, dynamic roadinformation and
 * route-choices due to traffic)
 * 
 */
startTime?: string | null,
/**
 * Set this parameter to avoid specific traffic situations, with a comma separated list of Situation IDs
 * 
 */
avoidSituationIds?: string | null,
/**
 * Set this parameter to avoid traffic messages of certain types, ie 'roadclosed' to avoid temporarily closed roads.
 * NB. Not yet fully supported.
 * 
 */
avoidTrafficMessageTypes?: string | null,
/**
 * Set this parameter to avoid all road features of the specified road feature type, ie 'Bomstasjon' to avoid toll stations.
 */
avoidRoadFeatureTypes?: Array<any> | null,
/**
 * Set this parameter to avoid a road feature instance/object, ie 'TrafficInformationSimple-SituationId'. Using the Attribute AvoidFeatureInstanceId
 */
avoidFeatureInstanceIds?: string | null,
/**
 * Set this parameter to include obstructed routes. Obstructed routes are calculated without restrictions, but cannot be driven because they are obstructed.
 * Default = false
 */
includeObstructedRoutes?: boolean,
}): CancelablePromise<RouteResponseOfRoadFeaturesWithBruksklasse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/Route/bruksklasseNormalTransport',
            query: {
                'Bruksklasse': bruksklasse,
                'Stops': stops,
                'InputSRS': inputSrs,
                'OutputSRS': outputSrs,
                'Lang': lang,
                'ReturnFields': returnFields,
                'Barriers': barriers,
                'StreetNameHints': streetNameHints,
                'EncapsulateStreetNames': encapsulateStreetNames,
                'AllowTravelInZeroEmissionZone': allowTravelInZeroEmissionZone,
                'Length': length,
                'Weight': weight,
                'Height': height,
                'StartTime': startTime,
                'AvoidSituationIds': avoidSituationIds,
                'AvoidTrafficMessageTypes': avoidTrafficMessageTypes,
                'AvoidRoadFeatureTypes': avoidRoadFeatureTypes,
                'AvoidFeatureInstanceIds': avoidFeatureInstanceIds,
                'IncludeObstructedRoutes': includeObstructedRoutes,
            },
        });
    }

    /**
     * Calculate a optimal route given the BruksklasseSpesialTransportRequest
     * @returns RouteResponseOfRoadFeaturesWithBruksklasse A valid route according to https://datakatalogen.atlas.vegvesen.no/#/902/Bruksklasse,%20spesialtransport
     * @throws ApiError
     */
    public static routeBruksklasseSpesialTransport({
bruksklasse,
stops,
veggruppe,
includeVeggruppeIkke,
inputSrs,
outputSrs,
lang,
returnFields,
barriers,
streetNameHints,
encapsulateStreetNames,
allowTravelInZeroEmissionZone,
length,
weight,
height,
startTime,
avoidSituationIds,
avoidTrafficMessageTypes,
avoidRoadFeatureTypes,
avoidFeatureInstanceIds,
includeObstructedRoutes,
}: {
bruksklasse: 'Bk_6_28' | 'Bk_8_32' | 'Bk_T8_40' | 'Bk_T8_50' | 'Bk_10_42' | 'Bk_10_50',
/**
 * A list of the included stops in the request. The stops need to be in the coordinate system corresponding to
 * InputSRS
 * ie: 236397.85827911127,6662445.118563663;237667.8608191164,6675145.143963715
 * 
 */
stops: string,
/**
 * Restricts the routing on a specific Veggruppe.
 * See Veggruppe property here: https://datakatalogen.atlas.vegvesen.no/#/902/Bruksklasse,%20spesialtransport
 * Defaults to "None"
 */
veggruppe?: 'None' | 'A' | 'B' | 'Ikke',
/**
 * When routing on Veggruppe A or B, use this option to also include Veggruppe "Ikke".
 * Defaults to false
 */
includeVeggruppeIkke?: boolean,
/**
 * Describes which coordinate system points in "stops" are using. Default is UTM 33N ( EPSG 25833 )
 * 
 */
inputSrs?: 'EPSG_25833' | 'EPSG_4326',
/**
 * Describes which coordinate system geometry in routedescription are using. Default is UTM 33N ( EPSG 25833 )
 * 
 */
outputSrs?: 'EPSG_25833' | 'EPSG_4326',
/**
 * Language for output directions-response.
 * 
 */
lang?: 'English' | 'Norwegian' | 'Swedish' | 'Deutch' | 'Finnish',
/**
 * List of additional Datafields to include in response
 * The textual directions for the route is always returned.
 * Note: SimpleGeometry is not implemented at the moment and will return the same result as Geometry.
 * 
 */
returnFields?: Array<any> | null,
/**
 * A list of the included barriers in the request. Barriers of type coordinate need to be in the UTM33N (EPSG:32633 or
 * EPSG:25833) coordinate system
 * 
 */
barriers?: string | null,
/**
 * streetnames for stops separated by ; (semicolon). By specifying a streetname for each stop in the stops-parameter
 * the router will try to snap the stops to streets with correct name
 * 
 */
streetNameHints?: string | null,
/**
 * If set to true, streetnames in returned directions are encapsulated in []. Ex: Go north on [skogsbilsveg] toward
 * [privat veg], Default=false
 * 
 */
encapsulateStreetNames?: boolean | null,
/**
 * Set this parameter to false to disallow travel in ZeroEmissionZones Default=true
 * 
 */
allowTravelInZeroEmissionZone?: boolean | null,
/**
 * If set, the route is calculated only using roads that allow a vehicle of the given length to pass. meters,
 * Default=0.0
 * 
 */
length?: number | null,
/**
 * If set, the route is calculated only using roads allowed to use with a vehicle of the specified weight in metric
 * tons. Should be given as number e.g. 15.6, Default=0.0
 * 
 */
weight?: number | null,
/**
 * If set, the route is calculated only using roads that allow a vehicle of the given height to pass. meters,
 * Default=0.0
 * 
 */
height?: number | null,
/**
 * format: yyyyMMddHHmmss Set the starttime of the route (this affects delays, dynamic roadinformation and
 * route-choices due to traffic)
 * 
 */
startTime?: string | null,
/**
 * Set this parameter to avoid specific traffic situations, with a comma separated list of Situation IDs
 * 
 */
avoidSituationIds?: string | null,
/**
 * Set this parameter to avoid traffic messages of certain types, ie 'roadclosed' to avoid temporarily closed roads.
 * NB. Not yet fully supported.
 * 
 */
avoidTrafficMessageTypes?: string | null,
/**
 * Set this parameter to avoid all road features of the specified road feature type, ie 'Bomstasjon' to avoid toll stations.
 */
avoidRoadFeatureTypes?: Array<any> | null,
/**
 * Set this parameter to avoid a road feature instance/object, ie 'TrafficInformationSimple-SituationId'. Using the Attribute AvoidFeatureInstanceId
 */
avoidFeatureInstanceIds?: string | null,
/**
 * Set this parameter to include obstructed routes. Obstructed routes are calculated without restrictions, but cannot be driven because they are obstructed.
 * Default = false
 */
includeObstructedRoutes?: boolean,
}): CancelablePromise<RouteResponseOfRoadFeaturesWithBruksklasse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/Route/bruksklasseSpesialTransport',
            query: {
                'Veggruppe': veggruppe,
                'IncludeVeggruppeIkke': includeVeggruppeIkke,
                'Bruksklasse': bruksklasse,
                'Stops': stops,
                'InputSRS': inputSrs,
                'OutputSRS': outputSrs,
                'Lang': lang,
                'ReturnFields': returnFields,
                'Barriers': barriers,
                'StreetNameHints': streetNameHints,
                'EncapsulateStreetNames': encapsulateStreetNames,
                'AllowTravelInZeroEmissionZone': allowTravelInZeroEmissionZone,
                'Length': length,
                'Weight': weight,
                'Height': height,
                'StartTime': startTime,
                'AvoidSituationIds': avoidSituationIds,
                'AvoidTrafficMessageTypes': avoidTrafficMessageTypes,
                'AvoidRoadFeatureTypes': avoidRoadFeatureTypes,
                'AvoidFeatureInstanceIds': avoidFeatureInstanceIds,
                'IncludeObstructedRoutes': includeObstructedRoutes,
            },
        });
    }

    /**
     * Calculate a optimal route given the BruksklasseTommerRequest
     * @returns RouteResponseOfRoadFeaturesWithBruksklasse A valid route according to https://datakatalogen.atlas.vegvesen.no/#/900/Bruksklasse,%20t%C3%B8mmertransport
     * @throws ApiError
     */
    public static routeBruksklasseTommerTransport({
bruksklasse,
stops,
inputSrs,
outputSrs,
lang,
returnFields,
barriers,
streetNameHints,
encapsulateStreetNames,
allowTravelInZeroEmissionZone,
length,
weight,
height,
startTime,
avoidSituationIds,
avoidTrafficMessageTypes,
avoidRoadFeatureTypes,
avoidFeatureInstanceIds,
includeObstructedRoutes,
}: {
bruksklasse: 'Bk_6_28' | 'Bk_8_32' | 'Bk_T8_40' | 'Bk_T8_50' | 'Bk_10_42' | 'Bk_10_50' | 'Bk_10_56' | 'Bk_10_60',
/**
 * A list of the included stops in the request. The stops need to be in the coordinate system corresponding to
 * InputSRS
 * ie: 236397.85827911127,6662445.118563663;237667.8608191164,6675145.143963715
 * 
 */
stops: string,
/**
 * Describes which coordinate system points in "stops" are using. Default is UTM 33N ( EPSG 25833 )
 * 
 */
inputSrs?: 'EPSG_25833' | 'EPSG_4326',
/**
 * Describes which coordinate system geometry in routedescription are using. Default is UTM 33N ( EPSG 25833 )
 * 
 */
outputSrs?: 'EPSG_25833' | 'EPSG_4326',
/**
 * Language for output directions-response.
 * 
 */
lang?: 'English' | 'Norwegian' | 'Swedish' | 'Deutch' | 'Finnish',
/**
 * List of additional Datafields to include in response
 * The textual directions for the route is always returned.
 * Note: SimpleGeometry is not implemented at the moment and will return the same result as Geometry.
 * 
 */
returnFields?: Array<any> | null,
/**
 * A list of the included barriers in the request. Barriers of type coordinate need to be in the UTM33N (EPSG:32633 or
 * EPSG:25833) coordinate system
 * 
 */
barriers?: string | null,
/**
 * streetnames for stops separated by ; (semicolon). By specifying a streetname for each stop in the stops-parameter
 * the router will try to snap the stops to streets with correct name
 * 
 */
streetNameHints?: string | null,
/**
 * If set to true, streetnames in returned directions are encapsulated in []. Ex: Go north on [skogsbilsveg] toward
 * [privat veg], Default=false
 * 
 */
encapsulateStreetNames?: boolean | null,
/**
 * Set this parameter to false to disallow travel in ZeroEmissionZones Default=true
 * 
 */
allowTravelInZeroEmissionZone?: boolean | null,
/**
 * If set, the route is calculated only using roads that allow a vehicle of the given length to pass. meters,
 * Default=0.0
 * 
 */
length?: number | null,
/**
 * If set, the route is calculated only using roads allowed to use with a vehicle of the specified weight in metric
 * tons. Should be given as number e.g. 15.6, Default=0.0
 * 
 */
weight?: number | null,
/**
 * If set, the route is calculated only using roads that allow a vehicle of the given height to pass. meters,
 * Default=0.0
 * 
 */
height?: number | null,
/**
 * format: yyyyMMddHHmmss Set the starttime of the route (this affects delays, dynamic roadinformation and
 * route-choices due to traffic)
 * 
 */
startTime?: string | null,
/**
 * Set this parameter to avoid specific traffic situations, with a comma separated list of Situation IDs
 * 
 */
avoidSituationIds?: string | null,
/**
 * Set this parameter to avoid traffic messages of certain types, ie 'roadclosed' to avoid temporarily closed roads.
 * NB. Not yet fully supported.
 * 
 */
avoidTrafficMessageTypes?: string | null,
/**
 * Set this parameter to avoid all road features of the specified road feature type, ie 'Bomstasjon' to avoid toll stations.
 */
avoidRoadFeatureTypes?: Array<any> | null,
/**
 * Set this parameter to avoid a road feature instance/object, ie 'TrafficInformationSimple-SituationId'. Using the Attribute AvoidFeatureInstanceId
 */
avoidFeatureInstanceIds?: string | null,
/**
 * Set this parameter to include obstructed routes. Obstructed routes are calculated without restrictions, but cannot be driven because they are obstructed.
 * Default = false
 */
includeObstructedRoutes?: boolean,
}): CancelablePromise<RouteResponseOfRoadFeaturesWithBruksklasse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/Route/bruksklasseTommerTransport',
            query: {
                'Bruksklasse': bruksklasse,
                'Stops': stops,
                'InputSRS': inputSrs,
                'OutputSRS': outputSrs,
                'Lang': lang,
                'ReturnFields': returnFields,
                'Barriers': barriers,
                'StreetNameHints': streetNameHints,
                'EncapsulateStreetNames': encapsulateStreetNames,
                'AllowTravelInZeroEmissionZone': allowTravelInZeroEmissionZone,
                'Length': length,
                'Weight': weight,
                'Height': height,
                'StartTime': startTime,
                'AvoidSituationIds': avoidSituationIds,
                'AvoidTrafficMessageTypes': avoidTrafficMessageTypes,
                'AvoidRoadFeatureTypes': avoidRoadFeatureTypes,
                'AvoidFeatureInstanceIds': avoidFeatureInstanceIds,
                'IncludeObstructedRoutes': includeObstructedRoutes,
            },
        });
    }

    /**
     * Calculate a optimal route given the BruksklasseModulvognTogRequest
     * @returns RouteResponseOfRoadFeaturesWithBruksklasse A valid route according to https://datakatalogen.atlas.vegvesen.no/#/889/Bruksklasse,%20modulvogntog
     * @throws ApiError
     */
    public static routeBruksklasseModulvognTog({
bruksklasse,
stops,
isLinkSemitrailer,
medSporingskrav,
inputSrs,
outputSrs,
lang,
returnFields,
barriers,
streetNameHints,
encapsulateStreetNames,
allowTravelInZeroEmissionZone,
length,
weight,
height,
startTime,
avoidSituationIds,
avoidTrafficMessageTypes,
avoidRoadFeatureTypes,
avoidFeatureInstanceIds,
includeObstructedRoutes,
}: {
bruksklasse: 'Bk_6_28' | 'Bk_8_32' | 'Bk_T8_40' | 'Bk_T8_50' | 'Bk_10_42' | 'Bk_10_50' | 'Bk_10_56' | 'Bk_10_60',
/**
 * A list of the included stops in the request. The stops need to be in the coordinate system corresponding to
 * InputSRS
 * ie: 236397.85827911127,6662445.118563663;237667.8608191164,6675145.143963715
 * 
 */
stops: string,
/**
 * If routing should be restricted to roads that allow vehicle type "LinkSemiTrailer"
 * see https://datakatalogen.atlas.vegvesen.no/#/889/Bruksklasse,%20modulvogntog
 * default is false
 */
isLinkSemitrailer?: boolean,
/**
 * If set to true, the route will be calculated according BruksklasseTommerTransport where the property "Tillatt for modulvogntog 1 og 2 med sporingskrav" = "Ja".
 * See https://datakatalogen.atlas.vegvesen.no/#/900/Bruksklasse,%20t%C3%B8mmertransport
 * default is false
 */
medSporingskrav?: boolean,
/**
 * Describes which coordinate system points in "stops" are using. Default is UTM 33N ( EPSG 25833 )
 * 
 */
inputSrs?: 'EPSG_25833' | 'EPSG_4326',
/**
 * Describes which coordinate system geometry in routedescription are using. Default is UTM 33N ( EPSG 25833 )
 * 
 */
outputSrs?: 'EPSG_25833' | 'EPSG_4326',
/**
 * Language for output directions-response.
 * 
 */
lang?: 'English' | 'Norwegian' | 'Swedish' | 'Deutch' | 'Finnish',
/**
 * List of additional Datafields to include in response
 * The textual directions for the route is always returned.
 * Note: SimpleGeometry is not implemented at the moment and will return the same result as Geometry.
 * 
 */
returnFields?: Array<any> | null,
/**
 * A list of the included barriers in the request. Barriers of type coordinate need to be in the UTM33N (EPSG:32633 or
 * EPSG:25833) coordinate system
 * 
 */
barriers?: string | null,
/**
 * streetnames for stops separated by ; (semicolon). By specifying a streetname for each stop in the stops-parameter
 * the router will try to snap the stops to streets with correct name
 * 
 */
streetNameHints?: string | null,
/**
 * If set to true, streetnames in returned directions are encapsulated in []. Ex: Go north on [skogsbilsveg] toward
 * [privat veg], Default=false
 * 
 */
encapsulateStreetNames?: boolean | null,
/**
 * Set this parameter to false to disallow travel in ZeroEmissionZones Default=true
 * 
 */
allowTravelInZeroEmissionZone?: boolean | null,
/**
 * If set, the route is calculated only using roads that allow a vehicle of the given length to pass. meters,
 * Default=0.0
 * 
 */
length?: number | null,
/**
 * If set, the route is calculated only using roads allowed to use with a vehicle of the specified weight in metric
 * tons. Should be given as number e.g. 15.6, Default=0.0
 * 
 */
weight?: number | null,
/**
 * If set, the route is calculated only using roads that allow a vehicle of the given height to pass. meters,
 * Default=0.0
 * 
 */
height?: number | null,
/**
 * format: yyyyMMddHHmmss Set the starttime of the route (this affects delays, dynamic roadinformation and
 * route-choices due to traffic)
 * 
 */
startTime?: string | null,
/**
 * Set this parameter to avoid specific traffic situations, with a comma separated list of Situation IDs
 * 
 */
avoidSituationIds?: string | null,
/**
 * Set this parameter to avoid traffic messages of certain types, ie 'roadclosed' to avoid temporarily closed roads.
 * NB. Not yet fully supported.
 * 
 */
avoidTrafficMessageTypes?: string | null,
/**
 * Set this parameter to avoid all road features of the specified road feature type, ie 'Bomstasjon' to avoid toll stations.
 */
avoidRoadFeatureTypes?: Array<any> | null,
/**
 * Set this parameter to avoid a road feature instance/object, ie 'TrafficInformationSimple-SituationId'. Using the Attribute AvoidFeatureInstanceId
 */
avoidFeatureInstanceIds?: string | null,
/**
 * Set this parameter to include obstructed routes. Obstructed routes are calculated without restrictions, but cannot be driven because they are obstructed.
 * Default = false
 */
includeObstructedRoutes?: boolean,
}): CancelablePromise<RouteResponseOfRoadFeaturesWithBruksklasse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/Route/bruksklasseModulvognTog',
            query: {
                'Bruksklasse': bruksklasse,
                'IsLinkSemitrailer': isLinkSemitrailer,
                'MedSporingskrav': medSporingskrav,
                'Stops': stops,
                'InputSRS': inputSrs,
                'OutputSRS': outputSrs,
                'Lang': lang,
                'ReturnFields': returnFields,
                'Barriers': barriers,
                'StreetNameHints': streetNameHints,
                'EncapsulateStreetNames': encapsulateStreetNames,
                'AllowTravelInZeroEmissionZone': allowTravelInZeroEmissionZone,
                'Length': length,
                'Weight': weight,
                'Height': height,
                'StartTime': startTime,
                'AvoidSituationIds': avoidSituationIds,
                'AvoidTrafficMessageTypes': avoidTrafficMessageTypes,
                'AvoidRoadFeatureTypes': avoidRoadFeatureTypes,
                'AvoidFeatureInstanceIds': avoidFeatureInstanceIds,
                'IncludeObstructedRoutes': includeObstructedRoutes,
            },
        });
    }

    /**
     * Calculate a optimal route given the RouteRequest
     * @returns RouteResponseOfRoadFeaturesWithBruksklasse A valid route according to https://datakatalogen.atlas.vegvesen.no/#/893/Bruksklasse,%2012/100-vegnett
     * @throws ApiError
     */
    public static routeBruksklasse12100({
stops,
inputSrs,
outputSrs,
lang,
returnFields,
barriers,
streetNameHints,
encapsulateStreetNames,
allowTravelInZeroEmissionZone,
length,
weight,
height,
startTime,
avoidSituationIds,
avoidTrafficMessageTypes,
avoidRoadFeatureTypes,
avoidFeatureInstanceIds,
includeObstructedRoutes,
}: {
/**
 * A list of the included stops in the request. The stops need to be in the coordinate system corresponding to
 * InputSRS
 * ie: 236397.85827911127,6662445.118563663;237667.8608191164,6675145.143963715
 * 
 */
stops: string,
/**
 * Describes which coordinate system points in "stops" are using. Default is UTM 33N ( EPSG 25833 )
 * 
 */
inputSrs?: 'EPSG_25833' | 'EPSG_4326',
/**
 * Describes which coordinate system geometry in routedescription are using. Default is UTM 33N ( EPSG 25833 )
 * 
 */
outputSrs?: 'EPSG_25833' | 'EPSG_4326',
/**
 * Language for output directions-response.
 * 
 */
lang?: 'English' | 'Norwegian' | 'Swedish' | 'Deutch' | 'Finnish',
/**
 * List of additional Datafields to include in response
 * The textual directions for the route is always returned.
 * Note: SimpleGeometry is not implemented at the moment and will return the same result as Geometry.
 * 
 */
returnFields?: Array<any> | null,
/**
 * A list of the included barriers in the request. Barriers of type coordinate need to be in the UTM33N (EPSG:32633 or
 * EPSG:25833) coordinate system
 * 
 */
barriers?: string | null,
/**
 * streetnames for stops separated by ; (semicolon). By specifying a streetname for each stop in the stops-parameter
 * the router will try to snap the stops to streets with correct name
 * 
 */
streetNameHints?: string | null,
/**
 * If set to true, streetnames in returned directions are encapsulated in []. Ex: Go north on [skogsbilsveg] toward
 * [privat veg], Default=false
 * 
 */
encapsulateStreetNames?: boolean | null,
/**
 * Set this parameter to false to disallow travel in ZeroEmissionZones Default=true
 * 
 */
allowTravelInZeroEmissionZone?: boolean | null,
/**
 * If set, the route is calculated only using roads that allow a vehicle of the given length to pass. meters,
 * Default=0.0
 * 
 */
length?: number | null,
/**
 * If set, the route is calculated only using roads allowed to use with a vehicle of the specified weight in metric
 * tons. Should be given as number e.g. 15.6, Default=0.0
 * 
 */
weight?: number | null,
/**
 * If set, the route is calculated only using roads that allow a vehicle of the given height to pass. meters,
 * Default=0.0
 * 
 */
height?: number | null,
/**
 * format: yyyyMMddHHmmss Set the starttime of the route (this affects delays, dynamic roadinformation and
 * route-choices due to traffic)
 * 
 */
startTime?: string | null,
/**
 * Set this parameter to avoid specific traffic situations, with a comma separated list of Situation IDs
 * 
 */
avoidSituationIds?: string | null,
/**
 * Set this parameter to avoid traffic messages of certain types, ie 'roadclosed' to avoid temporarily closed roads.
 * NB. Not yet fully supported.
 * 
 */
avoidTrafficMessageTypes?: string | null,
/**
 * Set this parameter to avoid all road features of the specified road feature type, ie 'Bomstasjon' to avoid toll stations.
 */
avoidRoadFeatureTypes?: Array<any> | null,
/**
 * Set this parameter to avoid a road feature instance/object, ie 'TrafficInformationSimple-SituationId'. Using the Attribute AvoidFeatureInstanceId
 */
avoidFeatureInstanceIds?: string | null,
/**
 * Set this parameter to include obstructed routes. Obstructed routes are calculated without restrictions, but cannot be driven because they are obstructed.
 * Default = false
 */
includeObstructedRoutes?: boolean,
}): CancelablePromise<RouteResponseOfRoadFeaturesWithBruksklasse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/Route/bruksklasse12100',
            query: {
                'Stops': stops,
                'InputSRS': inputSrs,
                'OutputSRS': outputSrs,
                'Lang': lang,
                'ReturnFields': returnFields,
                'Barriers': barriers,
                'StreetNameHints': streetNameHints,
                'EncapsulateStreetNames': encapsulateStreetNames,
                'AllowTravelInZeroEmissionZone': allowTravelInZeroEmissionZone,
                'Length': length,
                'Weight': weight,
                'Height': height,
                'StartTime': startTime,
                'AvoidSituationIds': avoidSituationIds,
                'AvoidTrafficMessageTypes': avoidTrafficMessageTypes,
                'AvoidRoadFeatureTypes': avoidRoadFeatureTypes,
                'AvoidFeatureInstanceIds': avoidFeatureInstanceIds,
                'IncludeObstructedRoutes': includeObstructedRoutes,
            },
        });
    }

    /**
     * Calculate a optimal route given the RouteRequest
     * @returns RouteResponseOfRoadFeaturesWithBruksklasse A valid route according to
 * https://datakatalogen.atlas.vegvesen.no/#/891/Bruksklasse,%2012/65%20mobilkran%20m.m.
 * 
     * @throws ApiError
     */
    public static routeBruksklasse1265({
stops,
inputSrs,
outputSrs,
lang,
returnFields,
barriers,
streetNameHints,
encapsulateStreetNames,
allowTravelInZeroEmissionZone,
length,
weight,
height,
startTime,
avoidSituationIds,
avoidTrafficMessageTypes,
avoidRoadFeatureTypes,
avoidFeatureInstanceIds,
includeObstructedRoutes,
}: {
/**
 * A list of the included stops in the request. The stops need to be in the coordinate system corresponding to
 * InputSRS
 * ie: 236397.85827911127,6662445.118563663;237667.8608191164,6675145.143963715
 * 
 */
stops: string,
/**
 * Describes which coordinate system points in "stops" are using. Default is UTM 33N ( EPSG 25833 )
 * 
 */
inputSrs?: 'EPSG_25833' | 'EPSG_4326',
/**
 * Describes which coordinate system geometry in routedescription are using. Default is UTM 33N ( EPSG 25833 )
 * 
 */
outputSrs?: 'EPSG_25833' | 'EPSG_4326',
/**
 * Language for output directions-response.
 * 
 */
lang?: 'English' | 'Norwegian' | 'Swedish' | 'Deutch' | 'Finnish',
/**
 * List of additional Datafields to include in response
 * The textual directions for the route is always returned.
 * Note: SimpleGeometry is not implemented at the moment and will return the same result as Geometry.
 * 
 */
returnFields?: Array<any> | null,
/**
 * A list of the included barriers in the request. Barriers of type coordinate need to be in the UTM33N (EPSG:32633 or
 * EPSG:25833) coordinate system
 * 
 */
barriers?: string | null,
/**
 * streetnames for stops separated by ; (semicolon). By specifying a streetname for each stop in the stops-parameter
 * the router will try to snap the stops to streets with correct name
 * 
 */
streetNameHints?: string | null,
/**
 * If set to true, streetnames in returned directions are encapsulated in []. Ex: Go north on [skogsbilsveg] toward
 * [privat veg], Default=false
 * 
 */
encapsulateStreetNames?: boolean | null,
/**
 * Set this parameter to false to disallow travel in ZeroEmissionZones Default=true
 * 
 */
allowTravelInZeroEmissionZone?: boolean | null,
/**
 * If set, the route is calculated only using roads that allow a vehicle of the given length to pass. meters,
 * Default=0.0
 * 
 */
length?: number | null,
/**
 * If set, the route is calculated only using roads allowed to use with a vehicle of the specified weight in metric
 * tons. Should be given as number e.g. 15.6, Default=0.0
 * 
 */
weight?: number | null,
/**
 * If set, the route is calculated only using roads that allow a vehicle of the given height to pass. meters,
 * Default=0.0
 * 
 */
height?: number | null,
/**
 * format: yyyyMMddHHmmss Set the starttime of the route (this affects delays, dynamic roadinformation and
 * route-choices due to traffic)
 * 
 */
startTime?: string | null,
/**
 * Set this parameter to avoid specific traffic situations, with a comma separated list of Situation IDs
 * 
 */
avoidSituationIds?: string | null,
/**
 * Set this parameter to avoid traffic messages of certain types, ie 'roadclosed' to avoid temporarily closed roads.
 * NB. Not yet fully supported.
 * 
 */
avoidTrafficMessageTypes?: string | null,
/**
 * Set this parameter to avoid all road features of the specified road feature type, ie 'Bomstasjon' to avoid toll stations.
 */
avoidRoadFeatureTypes?: Array<any> | null,
/**
 * Set this parameter to avoid a road feature instance/object, ie 'TrafficInformationSimple-SituationId'. Using the Attribute AvoidFeatureInstanceId
 */
avoidFeatureInstanceIds?: string | null,
/**
 * Set this parameter to include obstructed routes. Obstructed routes are calculated without restrictions, but cannot be driven because they are obstructed.
 * Default = false
 */
includeObstructedRoutes?: boolean,
}): CancelablePromise<RouteResponseOfRoadFeaturesWithBruksklasse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/Route/bruksklasse1265',
            query: {
                'Stops': stops,
                'InputSRS': inputSrs,
                'OutputSRS': outputSrs,
                'Lang': lang,
                'ReturnFields': returnFields,
                'Barriers': barriers,
                'StreetNameHints': streetNameHints,
                'EncapsulateStreetNames': encapsulateStreetNames,
                'AllowTravelInZeroEmissionZone': allowTravelInZeroEmissionZone,
                'Length': length,
                'Weight': weight,
                'Height': height,
                'StartTime': startTime,
                'AvoidSituationIds': avoidSituationIds,
                'AvoidTrafficMessageTypes': avoidTrafficMessageTypes,
                'AvoidRoadFeatureTypes': avoidRoadFeatureTypes,
                'AvoidFeatureInstanceIds': avoidFeatureInstanceIds,
                'IncludeObstructedRoutes': includeObstructedRoutes,
            },
        });
    }

}
